/* variant-picker.js - variant selection on the PDP, and the two other things
   the product form needs to be alive: the quantity stepper and the add.

   Selection is SERVER rendered. The picker is built from options, never from
   product.variants, and on change this file asks the Section Rendering API to
   re-render the section for the chosen option value ids. Price, availability,
   the sold out button state, the SKU line and pickup availability then all come
   back correct from ONE fetch, which is why there is no per field updater here.

   The section KEY is read from the DOM (data-section-id), never hardcoded: a
   section repainted through the Section Rendering API is coupled to its key in
   the template JSON, and a hardcoded key fails silently. The request succeeds,
   the section comes back absent, and the picker just stops updating.

   The stepper does NOT reuse the cart's data-quantity hooks on purpose:
   cart-items.js delegates on the document, intercepts [data-quantity-step] and
   would preventDefault a PDP click it can do nothing with, because the field
   carries no cart line key.

   A selection is announced OUTWARD as well as inward: a native change on the
   hidden id and Shopify's standard shopify:product:select. The installed apps
   listen there, not to PP.publish, and an app that never hears the switch goes
   on selling the first variant.

   init() is idempotent and re-runs on shopify:section:load. Every listener is
   delegated on the section root, so a patched region needs no rebinding. */
(function () {
  'use strict';

  var PP = window.PP;
  if (window.ppVariantPickerLoaded) return;
  window.ppVariantPickerLoaded = true;

  var ROOT = '[data-product-section]';
  /* A Map, NOT a WeakMap. The theme editor fires shopify:section:load on a
     re-render and fires NO unload, so a teardown reachable only through the old
     node never runs: its delegated listeners and any in flight Section
     Rendering fetch survive against a detached tree, once per merchant edit. An
     iterable registry can be swept for nodes that left the document. */
  var live = new Map();

  function sweep() {
    live.forEach(function (stop, node) {
      if (node.isConnected) return;
      live.delete(node);
      stop();
    });
  }

  function esc(value) {
    if (window.CSS && CSS.escape) return CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function destroy(root) {
    var stop = live.get(root);
    if (!stop) return;
    live.delete(root);
    stop();
  }

  function setup(root) {
    destroy(root);

    var off = [];
    var pending = null;
    /* The option value ids of the variant the SERVER last rendered. A failed
       repaint rolls the radios back to this, because a radio that shows one
       size while the price, the button and the hidden id all describe another
       is a page that will add something the shopper did not choose. */
    var loadedIds = [];

    function on(target, type, handler) {
      target.addEventListener(type, handler);
      off.push(function () {
        target.removeEventListener(type, handler);
      });
    }

    /* ---- variant selection ------------------------------------------------ */

    function optionValueIds() {
      return Array.prototype.slice
        .call(root.querySelectorAll('[data-option-value-id]'))
        .filter(function (input) {
          return input.checked;
        })
        .map(function (input) {
          return input.getAttribute('data-option-value-id');
        })
        .filter(Boolean);
    }

    function selectedVariant() {
      var holder = root.querySelector('[data-selected-variant]');
      if (!holder) return null;
      try {
        return JSON.parse(holder.textContent);
      } catch (error) {
        return null;
      }
    }

    /* Only the designated regions are swapped. Replacing the whole column
       would also replace the radio the shopper just pressed, and the browser
       drops focus onto the body when a focused node leaves the document.

       The buy region contains the quantity field and the error slot, and the
       server re-renders both at their defaults. Carrying them across the swap
       is not a nicety: a shopper who typed 3 and then changed size would
       otherwise be sent to checkout with 1, and an error being read aloud
       would vanish mid sentence. */
    function patch(fragment) {
      var field = root.querySelector('[data-quantity-field]');
      var quantity = field ? field.value : null;
      /* THE SUBSCRIPTION CHOICE CROSSES THE SWAP LIKE THE QUANTITY DOES. The
         Shopify Subscriptions widget appends its hidden selling_plan input to
         the buy form ONCE, when it starts, and afterwards only rewrites the
         value of the nodes it finds (Shopify/subscriptions-reference-app,
         app-block.js: appendSellingPlanInputs in the constructor,
         updateSellingPlanInputsValues on every change). The buy region below
         is swapped by innerHTML, form included, so without this the node was
         destroyed on the first size change and the form posted a ONE-TIME
         purchase for a shopper who had chosen to subscribe: no error, no
         trace, the wrong order. The nodes are detached before the swap and
         put back in the new form, the same nodes, so the widget keeps writing
         into them. Only nodes the server did not render are carried: the
         theme renders no selling_plan input of its own. */
      var buyForm = root.querySelector('[data-buy-form]');
      var plans = buyForm ? Array.prototype.slice.call(buyForm.querySelectorAll('input[name="selling_plan"]')) : [];
      plans.forEach(function (node) {
        node.parentNode.removeChild(node);
      });
      var slot = root.querySelector('[data-buy-error]');
      var message = slot && !slot.hidden ? slot.textContent : '';

      Array.prototype.forEach.call(fragment.querySelectorAll('[data-variant-region]'), function (fresh) {
        var name = fresh.getAttribute('data-variant-region');
        var target = null;
        try {
          target = root.querySelector('[data-variant-region="' + esc(name) + '"]');
        } catch (error) {
          return;
        }
        if (target) target.innerHTML = fresh.innerHTML;
      });

      if (quantity !== null) {
        var restored = root.querySelector('[data-quantity-field]');
        if (restored) restored.value = quantity;
      }
      if (plans.length) {
        var freshForm = root.querySelector('[data-buy-form]');
        plans.forEach(function (node) {
          if (freshForm && !freshForm.contains(node)) freshForm.appendChild(node);
        });
      }
      if (message) say(message);
      /* The rows come back from the server without it: the attribute is this
         file's answer about itself, not the server's. */
      markBridge();
    }

    /* One slot, one writer. It lives inside the form so a screen reader hears it
       next to the button it is about, but nothing outside this function ever
       writes to it. */
    function say(message) {
      var slot = root.querySelector('[data-buy-error]');
      if (!slot) return;
      slot.textContent = message || '';
      slot.hidden = !message;
    }

    /* THE REPAINT DESTROYS WHATEVER THE SHOPPER WAS STANDING ON, so the caret
       has to be put back by hand or a keyboard user is dropped on <body> in the
       middle of choosing a size. The radio is the first choice: it is the
       control that holds the state.

       IT IS NOT ALWAYS FOCUSABLE ANY MORE. Since blocks/variant-picker.liquid
       learned to hide itself (`hide_picker`, on by default in
       templates/product.json), the radios can sit under `display: none`, and
       `focus()` on a control in a hidden subtree is a silent no-op -- the call
       does not throw, it simply does nothing, and the only way to know is to
       read `document.activeElement` back. When that happens the press came from
       a size row in blocks/product-upsell.liquid, and the row bearing the same
       option value id is where the caret belongs: same id, same selection, and
       it is a link, so it takes focus. Asking the DOM rather than asking the
       stylesheet on purpose, because "can this take focus" is the question, and
       a class name is only ever a guess at it. */
    function refocus(optionValueId) {
      if (!optionValueId) return;
      var next = null;
      try {
        next = root.querySelector('[data-option-value-id="' + esc(optionValueId) + '"]');
      } catch (error) {
        return;
      }
      if (next && next !== document.activeElement) next.focus();
      if (next && next === document.activeElement) return;

      var row = null;
      try {
        row = root.querySelector('[data-option-value-select="' + esc(optionValueId) + '"]');
      } catch (error) {
        return;
      }
      if (row && row !== document.activeElement) row.focus();
    }

    function pushVariantUrl(variant) {
      /* The address bar belongs to the PAGE, not to this picker. On a product
         page rewriting it with ?variant= is the point: a reload, a share or a
         back button all land on the variant the shopper chose. Inside the quick
         add panel the shopper is standing on a collection or a search result,
         and stamping a product's variant id onto THAT url would send a shared
         or reloaded link somewhere nobody asked to go. The host says which it
         is, because only the host knows. */
      if (root.getAttribute('data-update-url') === 'false') return;
      if (!variant || !variant.id || !window.history || !history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      } catch (error) {
        /* An exotic URL costs the address bar, never the selection. */
      }
    }

    /* THE ROWS ROLL BACK WITH THE RADIOS, and leaving them out was a real hole.
       A row activation paints its own answer one frame early -- it moves
       aria-current before the fetch is even opened -- on the understanding that
       the server's re-render will say the same thing a moment later. When the
       repaint FAILS that re-render never arrives, and this function is what puts
       the page back: it used to put back the radios alone, which left the card
       showing a ring around the size the shopper pressed while the radio, the
       price, the hidden id and the buy button all still described the size the
       server has. One choice, two views, and the two views disagreeing is the
       exact condition the radio rollback exists to prevent.

       MEASURED by state/tools/verify-cover-pdp-multi-keyboard-mobile-nominal.mjs
       on 2026-09-14: under its picker-inert mutation the page ended with
       aria-current on one option value and the checked radio on the other, and
       it stayed that way for the readings after it.

       loadedIds holds the option value ids of the radios the SERVER last
       rendered, and a row names an option value id, so membership in it is
       exactly "this row is the selected one". */
    function rollback() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-option-value-id]'), function (input) {
        input.checked = loadedIds.indexOf(input.getAttribute('data-option-value-id')) !== -1;
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-option-value-select]'), function (row) {
        if (loadedIds.indexOf(row.getAttribute('data-option-value-select')) !== -1) {
          row.setAttribute('aria-current', 'true');
        } else {
          row.removeAttribute('aria-current');
        }
      });
    }

    /* ---- what the rest of the page is told ---------------------------------

       PP.publish reaches this theme's own modules and nothing else. The apps a
       pharmacy runs (subscriptions, bundles, upsell, reviews) were written
       against the two channels the platform standardises, and neither of them
       is ours: a native `change` on the hidden `input[name="id"]`, the Dawn era
       convention most installed apps still bind to, and `shopify:product:select`,
       the standard storefront event the pixel and agentic stacks read. Emitting
       neither is why an app keeps subscribing, bundling and reviewing the FIRST
       variant however many times the shopper changes size. */

    /* The variant id the outside world was last told about, seeded from the
       markup the server rendered so the first paint announces nothing: an app
       that counts `change` events would otherwise book a switch the shopper
       never made. The latch is not politeness either. An app that answers
       `change` by writing the id back comes straight back in here. */
    var announcedId = '';
    var announcedIds = '';
    var announcing = false;

    /* Property AND attribute, then `input` then `change`, in the order a real
       edit produces them: apps split about evenly between reading
       `input.value`, observing the attribute with a MutationObserver and
       binding one of the two events. Nothing is awaited between the write and
       the dispatch, because an app told one task late reads the id against a
       form the shopper may already have submitted.

       No `composed`: these controls are light DOM by architecture, and
       `composed` only buys an escape from a shadow tree there is none of. */
    function announceVariant(variantId) {
      var field = root.querySelector('[data-variant-id-input]');
      if (!field) return;
      /* The swapped in markup is the fallback: the id the server just wrote is
         authoritative even on the day the selected variant JSON does not parse,
         and an app is better told from the attribute than not told at all. */
      var value = String((variantId == null ? '' : variantId) || field.value || '');
      if (!value || announcing || value === announcedId) return;
      announcedId = value;
      announcing = true;
      try {
        field.value = value;
        field.setAttribute('value', value);
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        announcing = false;
      }
    }

    /* shopify:product:select carries a PROMISE for the resolved variant, which
       is the shape of this file exactly: the option ids go out now, the variant
       comes back from the Section Rendering API. Every dispatched event owes
       its listeners one settled promise, so both settlers are idempotent and
       the request's `finally` backstops a response this file abandoned. The
       rejection is caught here too, so a lookup Shopify refused cannot surface
       as an unhandled rejection in a shopper's console. What it resolves with
       is the section's own selected variant JSON, the only description of the
       variant this page holds; a theme that registers @shopify/standard-events
       builds that payload with the standard_event_data filter instead. */
    function lookup() {
      var settled = false;
      var box = {};
      box.promise = new Promise(function (resolve, reject) {
        box.resolve = function (result) {
          if (settled) return;
          settled = true;
          resolve(result);
        };
        box.fail = function (error) {
          if (settled) return;
          settled = true;
          reject(error);
        };
      });
      box.promise.catch(function () {});
      return box;
    }

    /* The product gid is in none of this section's markup. It is in the
       analytics meta Shopify injects through content_for_header, which is
       absent in the theme editor and in some preview contexts, so it is read
       defensively and the event goes out without it rather than not at all. The
       title is carried only by the variant's `name`, which is the product title
       with the public title appended. */
    function productPayload(variant) {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      var product = (meta && meta.product) || {};
      var path = (root.getAttribute('data-product-url') || '').split('?')[0].split('#')[0];
      var segments = path.split('/').filter(Boolean);
      var title = variant && variant.name ? String(variant.name) : '';
      var suffix = variant && variant.public_title ? ' - ' + variant.public_title : '';
      if (suffix && title.slice(-suffix.length) === suffix) title = title.slice(0, -suffix.length);
      return {
        id: product.gid || (product.id ? 'gid://shopify/Product/' + product.id : ''),
        title: title,
        handle: segments.length ? segments[segments.length - 1] : ''
      };
    }

    /* The option NAME is rendered once, as the legend's label, because the
       radios carry the value id the server resolves and deliberately nothing
       else. This is the only read in this file that touches the picker's markup
       rather than its data hooks, so a legend that moves costs the name, not
       the event. */
    function selectedOptions() {
      return Array.prototype.slice
        .call(root.querySelectorAll('[data-option-value-id]'))
        .filter(function (input) {
          return input.checked;
        })
        .map(function (input) {
          var group = input.closest ? input.closest('fieldset') : null;
          var label = group ? group.querySelector('legend > *') : null;
          return {
            name: label ? label.textContent.replace(/\s*:\s*$/, '').trim() : '',
            value: input.value
          };
        });
    }

    /* https://shopify.dev/docs/api/storefront-events-and-actions/events/product-select
       The event class ships in @shopify/standard-events, a CDN module a theme
       registers through an import map in layout. No layout file is this
       module's to write, so the class is used when a host has already put the
       library on window.StandardEvents and the same event is built by hand when
       it has not: listeners cannot tell them apart, only the development
       runtime's payload validation is lost. It is dispatched on the section
       root, the element that holds the option picker, and bubbles from there to
       document. */
    function announceSelect(ids) {
      var box = lookup();
      var key = ids.join(',');
      if (key === announcedIds) return box;
      announcedIds = key;
      try {
        var lib = window.StandardEvents;
        var payload = {
          product: productPayload(selectedVariant()),
          selectedOptions: selectedOptions(),
          promise: box.promise
        };
        var event;
        if (lib && lib.ProductSelectEvent) {
          event = new lib.ProductSelectEvent(payload);
        } else {
          event = new CustomEvent('shopify:product:select', { bubbles: true });
          event.product = payload.product;
          event.selectedOptions = payload.selectedOptions;
          event.promise = payload.promise;
        }
        root.dispatchEvent(event);
      } catch (error) {
        /* A library that changed shape costs the announcement and never the
           selection: the repaint is already in flight and the shopper is owed
           it whatever the apps on this page do. */
      }
      return box;
    }

    function select(optionValueId) {
      var sectionId = root.getAttribute('data-section-id');
      var productUrl = root.getAttribute('data-product-url');
      var ids = optionValueIds();
      if (!sectionId || !productUrl || !ids.length) return;

      var url;
      try {
        url = new URL(productUrl, window.location.origin);
      } catch (error) {
        return;
      }
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('option_values', ids.join(','));

      if (pending) pending.abort();
      var controller = new AbortController();
      pending = controller;
      root.setAttribute('aria-busy', 'true');

      /* Same task as the shopper's press, before the fetch and before anything
         is awaited: an app that has to know the options changed cannot be told
         a network round trip later. */
      var announced = announceSelect(ids);

      fetch(url.toString(), { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (controller !== pending) return;
          patch(new DOMParser().parseFromString(html, 'text/html'));
          var variant = selectedVariant();
          announceVariant(variant && variant.id);
          announced.resolve({ variant: variant || null });
          pushVariantUrl(variant);
          refocus(optionValueId);
          loadedIds = optionValueIds();
          say('');
          if (PP) PP.publish(PP.EVENTS.variantChange, { variant: variant, sectionId: sectionId });
        })
        .catch(function (error) {
          announced.fail(error);
          /* An abort is this file superseding itself; the newer request owns
             the outcome. Anything else is Shopify refusing (the Section
             Rendering API throttles hard, so 430 and 5xx are ordinary events)
             and the shopper must be told, not left with a selection the rest of
             the page does not share. */
          if (error && error.name === 'AbortError') return;
          if (controller !== pending) return;
          rollback();
          say(root.getAttribute('data-variant-error-text') || '');
        })
        .finally(function () {
          /* Ahead of the guard: a response this file walked away from is a
             lookup that never happened, and a listener holding the promise
             would otherwise wait for a variant that is never coming. */
          announced.fail(new Error('variant lookup superseded'));
          if (controller !== pending) return;
          pending = null;
          root.removeAttribute('aria-busy');
        });
    }

    on(root, 'change', function (event) {
      var input = event.target.closest ? event.target.closest('[data-option-value-id]') : null;
      if (!input) return;
      select(input.getAttribute('data-option-value-id'));
    });

    /* ---- the other things on the page that ask for a size ------------------

       A LINK THAT NAMES AN OPTION VALUE IS A SELECTION, NOT A NAVIGATION. The
       size rows under the buy button (blocks/product-upsell.liquid) ship as
       `<a href="?variant=">` so the page works with no script at all, and they
       carry data-option-value-select. Measured on 2026-09-13 on the dev theme:
       letting the href win cost a full document load, 2447 ms from press to
       painted page, against 15.1 ms for a pill in this picker. Same product,
       same size, same shop: the difference was entirely that one of them
       navigated.

       The row does not repaint anything itself. It checks the radio this picker
       already owns, which paints navy under the finger in the same frame through
       `:checked` alone, moves aria-current between the rows so the row the finger
       is on stops lying, and then runs the ordinary selection. One state, two
       views, and nothing here knows a class name from that block.

       A modified click is still the browser's: a new tab has to get a real URL,
       and a page with no picker (nothing answers the id) keeps the href too. */
    function bridgeTarget(id) {
      if (!id) return null;
      try {
        return root.querySelector('[data-option-value-id="' + esc(id) + '"]');
      } catch (error) {
        return null;
      }
    }

    /* THE FALLBACK HAS TO BE AUDIBLE. A row whose id resolves to no radio keeps
       its href and NAVIGATES, which is the floor this theme owes a page with no
       script -- and which measured 2447 ms against 13.5 to 47.6 ms for the
       bridged press, under a trusted input event across 375, 390 and 1440.
       A silent fallback is a regression with no symptom: the day a template
       ships the upsell block without blocks/variant-picker.liquid, the size rows
       go back to reloading the document and nothing anywhere says so. So the
       state is WRITTEN DOWN, per row, every time the markup changes: data-bridge
       is "on" when this file will take the press and "off" when the browser
       will. state/tools/verify-upsell-bridge.mjs reads it and refuses a mute
       one. Per row rather than per block on purpose -- the fallback is per row,
       and one attribute on a container would average two different answers. */
    function markBridge() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-option-value-select]'), function (row) {
        row.setAttribute('data-bridge', bridgeTarget(row.getAttribute('data-option-value-select')) ? 'on' : 'off');
      });
    }

    /* ONE ACTIVATION, TWO INPUT DEVICES. Extracted so that the finger and the
       keyboard run the SAME selection rather than two that drift: the callers
       differ only in which default action they have to suppress, and each of
       them suppresses it only when this returned true, so a row that resolves to
       no radio still gets the browser's own behaviour. */
    function activateRow(row) {
      if (!row) return false;
      var id = row.getAttribute('data-option-value-select');
      if (!id) return false;

      var input = bridgeTarget(id);
      if (!input) return false;

      Array.prototype.forEach.call(root.querySelectorAll('[data-option-value-select]'), function (other) {
        if (other === row) other.setAttribute('aria-current', 'true');
        else other.removeAttribute('aria-current');
      });

      if (!input.checked) {
        input.checked = true;
        select(id);
      }
      return true;
    }

    on(root, 'click', function (event) {
      if (event.defaultPrevented) return;
      if (event.button && event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var row = event.target.closest ? event.target.closest('[data-option-value-select]') : null;
      if (!activateRow(row)) return;
      event.preventDefault();
    });

    /* SPACE ON A SIZE ROW IS A CHOICE, NOT A PAGE DOWN. The rows stay `<a href>`
       so the page works with no script at all, and a link answers ENTER by
       itself: the browser synthesises a click and the handler above takes it.
       A link does NOT answer Space -- the default action there is to scroll the
       document -- and that is the whole gap this handler closes.

       MEASURED on the dev theme on 2026-09-14, Cicaplast Baume B5+ at 390 wide,
       keyboard only: with focus on the unselected row, Space left the hidden
       variant id at 44897092796531, left the price at $24.99, and moved the page
       from scrollY 502 to 1167. Nothing the shopper asked for happened and they
       lost their place by 665 px. Since blocks/variant-picker.liquid took the
       native pills off the screen wherever this card draws, these rows ARE the
       size control for a keyboard shopper, and a control that looks like a choice
       has to answer the two keys a choice answers.

       Enter is deliberately not handled here. The browser already turns it into a
       click on a link, so answering it a second time would run one press twice.
       The guards are the click handler's, for the same reasons: a modified press
       belongs to the browser, and the default scroll is suppressed only on a row
       this file actually took. */
    on(root, 'keydown', function (event) {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.key !== ' ' && event.key !== 'Spacebar') return;

      var row = event.target.closest ? event.target.closest('[data-option-value-select]') : null;
      if (!activateRow(row)) return;
      event.preventDefault();
    });

    /* ---- quantity ---------------------------------------------------------
       NOTHING HERE ANY MORE, AND THAT IS THE POINT. This file used to carry its
       own stepper handler, bound to a private `qty` prefixed step attribute,
       because the product form drew its own stepper behind that same private
       set of hooks. Both are gone:
       snippets/buy-buttons.liquid renders snippets/quantity-input.liquid like
       the cart does, and assets/cart-items.js answers `[data-quantity-step]`
       on the document for every surface, keyed field or not. Two handlers for
       one gesture is one handler too many, and the second one to be registered
       would have had to know it was second. What this file still owes the
       stepper is the carry across a variant repaint, in patch() above. */

    /* ---- add to cart ------------------------------------------------------ */

    /* The form posts to /cart/add on its own with this file absent, so the
       interception is the enhancement. On success the cart drawer is asked for,
       loading its module first if intent never armed it.

       PAINT BEFORE OPEN, and the order is the whole point. The drawer module is
       usually fetched BY the add that just changed the cart, so it subscribes a
       turn too late to hear the update it was loaded for, and the markup it
       first meets is the empty panel the page load rendered. It can recover on
       its own by comparing PP.cartVersion() against what it rendered and
       re-fetching the section, but that recovery is a second round trip racing a
       slide that has already started, and it is the only thing standing between
       the shopper and an empty drawer over a cart with something in it. So the
       add asks /cart/add.js for the section in the same breath and hands the
       markup over here: the panel is right before it is shown, and the
       staleness path stays as the fallback for when it is not.

       `html` absent is not a failure. Nothing is painted, the drawer is still
       stale, and open() re-fetches exactly as it did before. */
    function openDrawer(html) {
      if (!PP) return false;
      function show() {
        if (html && typeof PP.paintCartSection === 'function') PP.paintCartSection(html);
        if (PP.openCartDrawer) PP.openCartDrawer();
      }
      if (PP.openCartDrawer) {
        show();
        return true;
      }
      var panel = document.querySelector('[data-cart-drawer]');
      var src = panel && panel.getAttribute('data-cart-drawer-js');
      if (!src) return false;
      PP.loadModule(src).then(show);
      return true;
    }

    /* The drawer's Section Rendering key, read off the panel rather than
       hardcoded, so this file never learns where the section is rendered from.
       No panel means no key and no `sections` parameter, which is the correct
       request on a shop whose cart is a page. */
    function drawerSection() {
      var panel = document.querySelector('[data-cart-drawer]');
      return (panel && panel.getAttribute('data-section-id')) || '';
    }

    /* Two rules about what a shopper is allowed to read here: only Shopify's own
       `description` or `message`, or our localized fallback. NEVER an exception
       message. `/cart/add.js` answers with an HTML interstitial on a 429 or a
       502, and an unconditional response.json() then pipes a SyntaxError into
       the buy area as if it were copy: `Unexpected token '<', "<!DOCTYPE"...`.
       A network failure does the same with "Failed to fetch". The message is
       therefore carried on a property of our own, and anything without it falls
       back to the locale string. */
    function refuse(message) {
      var error = new Error('buy-buttons');
      error.shopperMessage = message || root.getAttribute('data-error-text') || '';
      return error;
    }

    /* EVERY submitter of this form, not just the one inside it. The sticky bar
       of snippets/sticky-atc-bar.liquid posts this same form from OUTSIDE it,
       associated by the HTML `form` attribute, so `form.querySelector` cannot
       see its button: a descendant query only ever finds descendants. That is
       why the lock below is built from `form.elements`, which the HTML spec
       fills with the form's listed elements INCLUDING the ones associated by
       the form attribute. Without this, the sticky button stayed enabled and
       un-busy for the whole /cart/add round trip and two fast taps posted the
       line twice, while the real button two screens up was correctly locked.
       The hooks are named one by one rather than matched on `type=submit`,
       because the dynamic checkout button the platform renders next to the add
       button is also a submitter and must never be disabled by us. */
    var SUBMIT_HOOKS = '[data-buy-submit], [data-sticky-atc-submit]';

    function submittersOf(form) {
      var found = [];
      var listed = form.elements;
      for (var i = 0; i < listed.length; i++) {
        var el = listed[i];
        if (el && el.matches && el.matches(SUBMIT_HOOKS)) found.push(el);
      }
      return found;
    }

    /* Is ANY submitter of this form already locked. It has to read the whole
       set: the shopper can start the add from either button, and the one they
       did not press is the one that would otherwise still accept a second
       press. Pulled out of the handler so the answer is a value the handler
       acts on, and not a control flow that can leave the handler entirely. */
    function anySubmitterLocked(buttons) {
      for (var b = 0; b < buttons.length; b++) {
        if (buttons[b].disabled) return true;
      }
      return false;
    }

    on(root, 'submit', function (event) {
      var form = event.target;
      /* The only two reasons to hand a submit BACK to the browser, and they are
         both about whether this file can act at all: the form is not ours, or
         PP never loaded so there is no bus, no module loader and no drawer to
         open. Both leave the native post to /cart/add intact, which is the
         progressive enhancement this whole interception is. The PP branch is
         reachable and not decoration: init(document) runs unconditionally at
         the bottom of this file, so the handler binds whether or not theme.js
         got there first. */
      if (!form.matches || !form.matches('[data-buy-form]') || !PP) return;

      /* PREVENTED HERE, BEFORE ANY REASON TO REFUSE, and that order IS the fix.
         The re-entry guard below used to sit above this line and leave by a
         bare `return`, so a submit that arrived while a submitter was disabled
         fell through to the NATIVE post: a full page navigation to /cart/add
         that throws the shopper out of the drawer, which is the opposite of
         what refusing an add is supposed to mean. Nothing between this line and
         the fetch is a reason to give the form back to the browser; every one
         of them is a reason to do nothing at all.

         It was harmless only while an invariant held elsewhere - the two
         submitters are rendered by the same server response and the lock moves
         them together, so they cannot disagree today - and nothing guarded that
         invariant. state/tools/verify-variant-picker-reentry.mjs guards it now,
         from the other end: it makes them disagree on purpose and asserts that
         no navigation follows.

         Unconditional preventDefault is safe beside the accelerated checkout
         button that `payment_button` renders INSIDE this form: that is a
         `shopify-accelerated-checkout` custom element which drives its own
         checkout, not a control that needs this form posted natively. Measured
         from the other side as well - every submit whose submitters were all
         enabled has always been prevented right here, so no accelerated path
         could ever have depended on the native post except inside the sold out
         or in flight window this change closes.
         doc: shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout */
      event.preventDefault();

      var buttons = submittersOf(form);
      /* Refusing an add is a no-op, never a fallback to the browser. */
      if (anySubmitterLocked(buttons)) return;
      say('');
      /* Only what we actually locked is unlocked in `finally`. A button that
         was already disabled when the submit began (a variant that went out of
         stock under the shopper) must stay disabled after the response. */
      var locked = [];
      for (var b = 0; b < buttons.length; b++) {
        buttons[b].setAttribute('aria-busy', 'true');
        buttons[b].disabled = true;
        locked.push(buttons[b]);
      }

      /* `sections_url` is the page the section is rendered AGAINST, not the
         endpoint: Liquid needs a route to resolve the drawer's own links from,
         and the current path is the honest one. Both parameters are dropped
         when there is no drawer, so the request stays the plain add it was. */
      var body = new FormData(form);
      var section = drawerSection();
      if (section) {
        body.append('sections', section);
        body.append('sections_url', window.location.pathname);
      }

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: body
      })
        .then(function (response) {
          /* NEVER SNIFF THE CONTENT TYPE HERE. This used to read the header and
             refuse anything without 'json' in it, which inverted the whole
             feature: measured on the shop, /cart/add.js answers a SUCCESS with
             `text/javascript; charset=utf-8` and a FAILURE with
             `application/json`. So the guard passed exactly when the add had
             failed and threw exactly when it had worked. Every successful add
             was reported to the shopper as "your cart could not be updated"
             while the item really was in the cart, the drawer never opened and
             the badge never moved.

             The HTML interstitial a 429 or a 502 returns is still handled, and
             by the thing that actually knows: response.json() REJECTS on it,
             and the rejection callback below turns that into our own localized
             refusal rather than letting `Unexpected token '<'` reach the buy
             area as if it were copy. Parsing is the test; the header is only a
             claim about the parse. */
          return response.json().then(
            function (body) {
              if (response.ok) return body;
              throw refuse(body && (body.description || body.message));
            },
            function () {
              throw refuse('');
            }
          );
        })
        .then(function (payload) {
          /* Published FIRST and without a number, exactly as before. It is what
             increments PP.cartVersion(), which is how a drawer module that has
             not loaded yet finds out it was already out of date. The count
             itself arrives a moment later, off the section markup, from the
             paint inside openDrawer(). */
          PP.publish(PP.EVENTS.cartUpdate, { source: 'buy-buttons' });
          var sections = payload && payload.sections;
          var html = section && sections ? sections[section] : null;
          if (!openDrawer(html)) window.location.href = root.getAttribute('data-cart-url') || '/cart';
        })
        .catch(function (error) {
          say(error && error.shopperMessage ? error.shopperMessage : root.getAttribute('data-error-text') || '');
          PP.publish(PP.EVENTS.cartError, { source: 'buy-buttons' });
        })
        .finally(function () {
          for (var u = 0; u < locked.length; u++) {
            locked[u].removeAttribute('aria-busy');
            locked[u].disabled = false;
          }
        });
    });

    markBridge();

    loadedIds = optionValueIds();
    /* Seeded, never announced. setup() also runs on a theme editor repaint and
       on a quick add panel injected after load, and an id that only just
       arrived in the DOM is not a variant the shopper switched to. */
    announcedIds = loadedIds.join(',');
    var seed = root.querySelector('[data-variant-id-input]');
    announcedId = seed ? String(seed.value || '') : '';

    live.set(root, function () {
      if (pending) pending.abort();
      pending = null;
      off.forEach(function (fn) {
        fn();
      });
      off = [];
    });
  }

  function init(scope) {
    sweep();
    var node = scope && scope.querySelectorAll ? scope : document;
    if (node.matches && node.matches(ROOT)) setup(node);
    Array.prototype.forEach.call(node.querySelectorAll(ROOT), setup);
  }

  init(document);

  /* Published so a host that INJECTS a product form after page load can wire
     it: assets/quick-add.js drops the panel's markup into the dialog and this
     module has no way to notice, since nothing here observes the DOM and
     shopify:section:load is the editor's event, not an ajax one. Idempotent, so
     calling it on a scope that is already live is free. */
  if (PP) PP.initVariantPicker = init;

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var node = event.target;
    if (node && node.querySelectorAll) Array.prototype.forEach.call(node.querySelectorAll(ROOT), destroy);
    sweep();
  });
})();
